/* ==========================================================================
   Gemians - Regular Adv prelander

   Scroll reveal for the sidebar and the sticky bar, the product-image slider,
   and the lazy video facade. Vanilla, deferred, no dependency on the theme's
   runtime - this page does not load it.

   The source did the reveal with a scroll listener that called
   getBoundingClientRect() on every frame. Same behaviour here, but an
   IntersectionObserver does it without measuring the DOM while the reader is
   scrolling.
   ========================================================================== */
(function () {
  'use strict';

  var reduceMotion =
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function $all(selector, scope) {
    return Array.prototype.slice.call((scope || document).querySelectorAll(selector));
  }

  /* ------------------------------------------------------------------------
     Scroll reveal

     `data-threshold="0.7"` means "fire once the trigger's top has risen into
     the top 70% of the viewport", which is what the source's
     `rect.top < innerHeight * 0.7` did. As a root margin that is a -30%
     bottom inset.
     ---------------------------------------------------------------------- */
  function onReveal(triggerSelector, threshold, callback) {
    var trigger = triggerSelector ? document.querySelector(triggerSelector) : null;

    // No trigger on the page (client removed that section, or mistyped the
    // selector): reveal immediately rather than never.
    if (!trigger || !('IntersectionObserver' in window)) {
      callback();
      return;
    }

    // The source's own test, in one place: true when the trigger is at or above
    // the line, which includes being long past it.
    function past() {
      return trigger.getBoundingClientRect().top < window.innerHeight * threshold;
    }

    // Already past it before we even ran - a link straight to an anchor further
    // down, for one.
    if (past()) {
      callback();
      return;
    }

    var revealed = false;

    function fire() {
      if (revealed) return;
      revealed = true;
      observer.disconnect();
      window.removeEventListener('scroll', onScroll);
      callback();
    }

    // The backstop, and the reason it is needed: an observer only reports a
    // *change* of intersection. Jump the whole band in one go - an anchor link,
    // a scrollbar drag, a hard flick - and the trigger goes from below it to
    // above it without ever being inside, so no entry is delivered and nothing
    // fires. It stays hidden for the rest of the page, because the state never
    // changes back either. Measured on the live theme: at the blog page's
    // one-pixel band, a single scrollTo past it never revealed the bar again.
    // One rect read per frame, only until the reveal happens, then removed.
    var ticking = false;
    function onScroll() {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(function () {
        ticking = false;
        if (past()) fire();
      });
    }

    var inset = Math.round((1 - threshold) * 100);
    var observer = new IntersectionObserver(
      function (entries) {
        for (var i = 0; i < entries.length; i++) {
          var entry = entries[i];

          // Not `isIntersecting`. The source's test was
          // `rect.top < innerHeight * 0.7`, true both when the trigger has just
          // risen into that band and when the reader is already past it - and
          // an element above the viewport never intersects again. Asking only
          // about intersection left the sidebar hidden for the whole page
          // whenever the reader arrived below the trigger: a reload restoring
          // the scroll position, the back button, a deep link. Chrome restores
          // that scroll after DOMContentLoaded, so the check above is too early
          // to catch it, but the observer's first entry lands after it.
          // `rootBounds.bottom` is the same line the source measured against.
          var line = entry.rootBounds
            ? entry.rootBounds.bottom
            : window.innerHeight * threshold;

          if (entry.isIntersecting || entry.boundingClientRect.top < line) {
            fire();
            return;
          }
        }
      },
      { rootMargin: '0px 0px -' + inset + '% 0px', threshold: 0 }
    );

    observer.observe(trigger);
    window.addEventListener('scroll', onScroll, { passive: true });
  }

  function initSidebar() {
    var sidebar = document.querySelector('[data-gemians-adv-sidebar]');
    if (!sidebar) return;

    var layout = document.querySelector('[data-gemians-adv-layout]');

    onReveal(
      sidebar.getAttribute('data-trigger'),
      parseFloat(sidebar.getAttribute('data-threshold')) || 0.7,
      function () {
        sidebar.classList.add('gemians-adv-sidebar--visible');
        if (layout) layout.classList.add('gemians-adv-layout--sidebar');
      }
    );
  }

  function initBar() {
    var bar = document.querySelector('[data-gemians-adv-bar]');
    if (!bar) return;

    onReveal(
      bar.getAttribute('data-trigger'),
      parseFloat(bar.getAttribute('data-threshold')) || 0.7,
      function () {
        bar.classList.add('gemians-adv-bar--visible');
      }
    );
  }

  /* ------------------------------------------------------------------------
     Image slider - scroll-snap carousel with arrows and dots
     ---------------------------------------------------------------------- */
  function initSlider(root) {
    var track = root.querySelector('[data-gemians-adv-slider-track]');
    if (!track) return;

    var dots = $all('[data-gemians-adv-slider-dot]', root);
    var slides = $all('[data-gemians-adv-slider-slide]', root);
    if (!slides.length) return;

    // Each slide's own offset rather than index x track width. `offsetWidth` is
    // rounded to a whole pixel and the column is not: at 375 it is 353.3125, so
    // three slides in the arithmetic version is already 1px adrift of where the
    // slide actually sits, and it drifts further the further you go.
    function slideOffset(index) {
      return slides[index].offsetLeft - slides[0].offsetLeft;
    }

    function currentIndex() {
      var best = 0;
      var bestDelta = Infinity;
      for (var i = 0; i < slides.length; i++) {
        var delta = Math.abs(slideOffset(i) - track.scrollLeft);
        if (delta < bestDelta) {
          bestDelta = delta;
          best = i;
        }
      }
      return best;
    }

    function paintDots(index) {
      dots.forEach(function (dot, i) {
        dot.classList.toggle('gemians-adv-slider__dot--active', i === index);
        dot.setAttribute('aria-current', i === index ? 'true' : 'false');
      });
    }

    // Where the reader is heading, which is not the same as where the carousel
    // currently is. The arrows step from this: reading the live position
    // instead means a second click that lands mid-animation measures a slide
    // half way past and steps from the wrong one.
    var intent = currentIndex();

    function goTo(index) {
      var target = Math.max(0, Math.min(slides.length - 1, index));
      intent = target;

      // Mark the dot now rather than waiting for the scroll to arrive. A smooth
      // scroll reports its position as it travels, so a second click lands
      // while the first is still moving and reads a half-way index; and if the
      // animation is interrupted - another click, a swipe, a tab that was not
      // being painted - the dots are left pointing at the slide the reader was
      // on before. The scroll handler below still corrects this for swipes.
      paintDots(target);

      track.scrollTo({
        left: slideOffset(target),
        behavior: reduceMotion ? 'auto' : 'smooth'
      });
    }

    dots.forEach(function (dot, index) {
      dot.addEventListener('click', function () {
        goTo(index);
      });
    });

    $all('[data-gemians-adv-slider-arrow]', root).forEach(function (arrow) {
      arrow.addEventListener('click', function (event) {
        event.preventDefault();
        goTo(intent + (arrow.getAttribute('data-gemians-adv-slider-arrow') === 'prev' ? -1 : 1));
      });
    });

    // Swipes, and anything that leaves the carousel somewhere the buttons did
    // not put it. Read once the movement has settled rather than on every
    // frame of it: a smooth scroll passes over the slides in between, and
    // repainting the dots at each of those makes them flicker through the set
    // on the way to the one that was asked for.
    var settle = null;
    track.addEventListener(
      'scroll',
      function () {
        if (settle) clearTimeout(settle);
        settle = setTimeout(function () {
          intent = currentIndex();
          paintDots(intent);
        }, 90);
      },
      { passive: true }
    );
  }

  /* ------------------------------------------------------------------------
     Lazy video facade

     Nothing is fetched until the player is near the viewport - the source
     autoplayed four .mp4 files, 8.9 MB together, on load. Playback pauses
     again when the player scrolls away, and a click toggles it, as in the
     source. A reader who has asked for reduced motion gets the poster and the
     play button instead of a clip that starts moving on its own.
     ---------------------------------------------------------------------- */
  function initVideo(holder) {
    var video = holder.querySelector('video');
    var src = holder.getAttribute('data-src');
    if (!video || !src) return;

    var autoplay = holder.getAttribute('data-autoplay') === 'true' && !reduceMotion;
    var loaded = false;

    video.muted = true;

    function load() {
      if (loaded) return;
      loaded = true;
      video.src = src;
    }

    function play() {
      load();
      var promise = video.play();
      // Rejected when the browser blocks playback. The poster stays up, and
      // the play button with it, which is the correct fallback.
      if (promise && promise.catch) promise.catch(function () {});
    }

    video.addEventListener('playing', function () {
      holder.classList.add('gemians-adv-video--playing');
    });
    video.addEventListener('pause', function () {
      holder.classList.remove('gemians-adv-video--playing');
    });

    holder.addEventListener('click', function () {
      if (video.paused) {
        holder.removeAttribute('data-paused-by-reader');
        play();
      } else {
        video.pause();
        holder.setAttribute('data-paused-by-reader', '');
      }
    });

    if (!('IntersectionObserver' in window)) return;

    new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            if (autoplay && !holder.hasAttribute('data-paused-by-reader')) play();
          } else if (!video.paused) {
            video.pause();
          }
        });
      },
      { rootMargin: '200px 0px', threshold: 0.01 }
    ).observe(holder);
  }

  /* ------------------------------------------------------------------------
     Boot
     ---------------------------------------------------------------------- */
  function init(scope) {
    initSidebar();
    initBar();
    $all('[data-gemians-adv-slider]', scope).forEach(initSlider);
    $all('[data-gemians-adv-video]', scope).forEach(initVideo);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init(document);
    });
  } else {
    init(document);
  }

  // Re-run for a section the client has just edited in the theme editor.
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
})();
